<template>
  <div class="search">
    <p class="search__static-downloads">
      <a :href="downloadRestrictionsPdfUrl" target="_blank">Download der Rechterückrufe als PDF</a><br>
      <a :href="downloadCompletePdfUrl" target="_blank">Download aller Labelcodes als PDF</a>
    </p>
    <form action="." method="get">
      <table class="search__form" aria-describedby="Search for labelcodes">
        <thead class="search__form__header">
          <SearchHeader ref="searchHeader" :search="search" />
          <SearchForm />
          <SearchResultFormButtons :filteredResults="filteredResults" :totalPages="totalPages" />
        </thead>
        <SearchResultEmpty :filteredResults="filteredResults" />
        <SearchResultRows :filteredResults="filteredResults" />
        <SearchResultFooter :filteredResults="filteredResults" :totalPages="totalPages" :totalResults="totalResults" :getPaginationButtonList="getPaginationButtonList" />
      </table>
    </form>
    <div class="search__footer">
      <p>
        <strong>Hinweis:</strong> Informationen zur Nutzungsbeschränkung
        erhalten Sie unter folgender Adresse:
        <a
          href="https://www.gvl.de/rechtenutzer/labels-labelcode"
          target="_blank"
          rel="noopener noreferrer"
          >www.gvl.de/rechtenutzer/labels-labelcode</a>.
      </p>
    </div>
    <SearchResultModal
      v-show="isModalVisible"
      @close="closeModal"
      :toDisplayRightsRestrictions="filteredRestrictions"
      :restrictionsInformationTitle="restrictionsInformationTitle"
    />
  </div>
</template>

<script>
import SearchHeader from './Header'
import SearchForm from './Form'
import SearchResultFormButtons from './ResultFormButtons'
import SearchResultEmpty from './ResultEmpty'
import SearchResultRows from './ResultRows'
import SearchResultFooter from './ResultFooter'
import SearchResultModal from './ResultModal.vue'

import axios from 'axios'
import debounce from 'debounce'
import SearchFormModel from './Model.class.ts'

const URL_DATA = '/api/v1/labelcodes?'
const URL_PDF = '/api/v1/labelcodes/pdf?'
const URL_PDF_ALL_LABEL_CODES = '/pdf/all-labelcodes.pdf'
const URL_RESTRICTIONS = '/json/all-rights-restrictions'
const URL_RESTRICTIONS_PDF = '/pdf/all-rights-restrictions.pdf'

export default {
  name: 'Search',
  components: {
    SearchHeader,
    SearchForm,
    SearchResultFormButtons,
    SearchResultEmpty,
    SearchResultRows,
    SearchResultFooter,
    SearchResultModal
  },
  props: {},
  data () {
    return {
      // 'search' gets populated by child ./Form.component to be able to pass queryParameters to other child ./Results
      search: null,
      isBusy: false,
      responseData: [],
      totalPages: 0,
      totalResults: 0,
      pdfDownloadType: 'current',
      isModalVisible: false,
      rightsRestricionsComplete: [],
      filteredRestrictions: [],
      restrictionsInformationTitle: '',
      downloadCompletePdfUrl: URL_PDF_ALL_LABEL_CODES,
      downloadRestrictionsPdfUrl: URL_RESTRICTIONS_PDF
    }
  },
  mounted () {
    // save a copy of initial state of SearchForm
    this.previousSearch = new SearchFormModel(this.search)
    this.doSearch = debounce(this.doSearch, 300)
    this.loadRightsRestrictions()
  },
  computed: {
    filteredResults () {
      if (this.isWorthToSearchFor()) {
        this.$log.debug(
          'search: ', this.search,
          'previousSearch: ', this.previousSearch,
          'compare unequal: ', this.search.hash(), '!==', this.previousSearch.hash(), (this.search.hash() !== this.previousSearch.hash())
        )
        this.$log.info('Would trigger ajax-search with', this.search)
        this.doSearch()
      } else {
        this.$log.info('Would NOT trigger ajax-search with', this.search)
      }
      return this.responseData
    },
    isPreviousButtonDisabled () {
      return this.search._page === 1
    },
    isNextButtonDisabled () {
      return this.search._page === this.totalPages
    },
    getPaginationButtonList () {
      if (this.totalPages > 9) {
        if (this.search._page <= 6) {
          this.$log.debug('getPaginationButtonList: _page <= 6 : ellipsis at end')
          return [1, 2, 3, 4, 5, 6, 7, '… ', this.totalPages]
        } else if (this.search._page > 6 && this.search._page < this.totalPages - 5) {
          this.$log.debug('getPaginationButtonList: ellipsis at start and end')
          return [1, ' …', (this.search._page - 2), (this.search._page - 1), this.search._page, (this.search._page + 1), (this.search._page + 2), '… ', this.totalPages]
        } else if (this.search._page > 6 && this.search._page >= this.totalPages - 5) {
          this.$log.debug('getPaginationButtonList: _page > 6 && _page >= ' + (this.totalPages - 5) + ' : ellipsis at start')
          return [1, ' …', (this.totalPages - 6), (this.totalPages - 5), (this.totalPages - 4), (this.totalPages - 3), (this.totalPages - 2), (this.totalPages - 1), this.totalPages]
        }
      } else if (this.totalPages <= 9 && this.totalPages > 1) {
        this.$log.debug('getPaginationButtonList: exact 9 pages or less')
        return Array(this.totalPages).fill().map((v, i) => 1 + i)
      }
      return []
    }
  },
  methods: {
    isWorthToSearchFor () {
      this.$log.debug('search: ', this.search, 'previousSearch: ', this.previousSearch)
      return !this.isBusy &&
        this.search &&
        this.previousSearch &&
        this.search.hash() !== this.previousSearch.hash()
    },
    doSearch () {
      this.isBusy = true
      if (this.search.hashOfMainProperties() !== this.previousSearch.hashOfMainProperties()) {
        // need to reset page, 'cause search basically changed
        this.search._page = this.previousSearch._page = 1
      }
      const currentSearch = this.search
      const url = URL_DATA + currentSearch.getAsQuery()
      this.$log.debug('url: ', url)
      axios
        .get(url)
        .then((response) => {
          this.$log.info('response to ', url, ': ', response)
          this.responseData = response.data
          this.totalPages = parseInt(response.headers['x-total-pages'])
          this.totalResults = parseInt(response.headers['x-total'])
          this.previousSearch = new SearchFormModel(currentSearch)
          this.isBusy = false
          this.$log.debug('this.totalPages: ', this.totalPages, (typeof this.totalPages), 'this.totalResults: ', this.totalResults, (typeof this.totalResults))
        }, (error) => {
          this.$log.error('Error requesting {url}:'.replace(/\{url\}/, url), error)
          this.responseData = []
          this.previousSearch = new SearchFormModel(currentSearch)
          this.isBusy = false
        })
    },
    previousPage () {
      this.search._page--
      this.doSearch()
    },
    nextPage () {
      this.search._page++
      this.doSearch()
    },
    goToPage (evnt) {
      this.search._page = parseInt(evnt.target.dataset.page)
    },
    downloadPdf () {
      let url = '#'
      switch (this.pdfDownloadType) {
        case 'current':
          if (this.previousSearch && this.totalPages) {
            url = URL_PDF + this.previousSearch.getAsQuery()
          }
          break
        case 'search':
          if (this.previousSearch && this.totalPages) {
            const currentSearch = new SearchFormModel(this.previousSearch)
            currentSearch._perPage = undefined
            currentSearch._page = undefined
            url = URL_PDF + currentSearch.getAsQuery()
            this.$log.debug('Download-URL before replacing: ', url)
          }
          break
      }
      this.$log.info('Download-URL: ', url)
      window.location.href = url
      return false
    },
    resetForm () {
      this.search.reset()
      this.responseData = []
      this.totalPages = 0
      this.totalResults = 0
      this.previousSearch = new SearchFormModel(this.search)
      const sortedHeaders = [this.$refs.searchHeader.$el.querySelector('.asc'),
        this.$refs.searchHeader.$el.querySelector('.desc')]
      for (let i = 0; i < sortedHeaders.length; i++) {
        const sortedHeader = sortedHeaders[i]
        if (sortedHeader) {
          sortedHeader.className = sortedHeader.className.replace(/(\sasc|\sdesc)/, '')
        }
      }
    },
    loadRightsRestrictions () {
      axios
        .get(URL_RESTRICTIONS)
        .then((result) => {
          this.$log.debug('Requested rights restrictions', result)
          this.rightsRestricionsComplete = result.data.rightsRecalls
        }, (error) => {
          this.$log.error('Error requesting {url}:'.replace(/\{url\}/, URL_RESTRICTIONS), error)
        })
    },
    showModal (rightsRestrictionsText, labelCode) {
      this.$log.debug('this.rightsRestricionsComplete: ', this.rightsRestricionsComplete, 'rightsRestrictionsText: ', rightsRestrictionsText)
      rightsRestrictionsText = rightsRestrictionsText.replace(/ /, '') + ','
      this.filteredRestrictions = this.rightsRestricionsComplete.filter(rightsRestricion => rightsRestrictionsText.indexOf(rightsRestricion.code + ',') > -1)
      this.$log.debug('this.filteredRestrictions: ', this.filteredRestrictions)

      this.$log.debug('this.responseData: ', this.responseData)
      // assuming this always just can be one!
      const labelCodeData = this.responseData.filter((item) => item.labelCode === labelCode)[0]
      this.$log.debug('labelCodeData: ', labelCodeData)
      this.restrictionsInformationTitle = labelCodeData.labelCode + ' "' + labelCodeData.label + '" von ' + labelCodeData.companyName
      this.isModalVisible = true
    },
    closeModal () {
      this.isModalVisible = false
      this.filteredRestrictions = []
      this.restrictionsInformationTitle = ''
    }
  }
}
</script>

<style lang="scss">
  .search {
    margin: 90px auto 160px auto;
    overflow: hidden;
    padding: 1%;
    text-align: left;
    width: 90%;

    .search__static-downloads {
      display: inline-block;
      float: right;
      margin-bottom: 2em;
      clear: right;
    }

    .search__form {
      clear: both;
      width: 100%;
    }

    .search__result {

      tr {
        td {
          border-bottom: 1px solid black;
          border-right: none;
          height: 3em;
          padding: 0 1%;

          &:last-child {
            border-right: none;
          }
        }
        &:nth-child(even) {
          background-color: #e8e8e8;
        }
        &:nth-child(odd) {
          background-color: #ffffff;
        }
      }

      &.search__result--empty {
        tr td {
          border: none;
        }
      }

      .search__result__td--labelCode {
        width: 9.5%;
      }
      .search__result__td--label  {
        width: 19.5%;
      }
      .search__result__td--shortName {
        width: 9.5%;
      }
      .search__result__td--plm {
        width: 5%;
        text-align: center;

        span {
          background-color: #ccc;
          border: 1px solid #ccc;
          border-radius: 1em;
          color: white;
          display: inline-block;
          font-weight: 700;
          height: 1.3em;
          margin: 0.2em;
          padding: 0.2em;
          width: 1.3em;
        }
      }
      .search__result__td--producer {
        width: 39%;
      }
      .search__result__td--rightrecalls {
        width: 14.5%;
        color: #aa3c14;
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
</style>
